/**
 * Text Parser Service
 * 
 * Centralized utilities for parsing and manipulating drawing designation text.
 * Handles extraction of prefix, designation, and value from keyed text format.
 */

import { DESIGNATION_PLACEHOLDER, SERVER_DESIGNATION_PLACEHOLDER } from '../constants'

/**
 * Parsed text structure for Product Type with drawing designation
 */
export interface ParsedDesignationText {
    /** Text before the parentheses (e.g., "A. Clear Annealed Float Glass") */
    prefix: string
    /** Drawing designation value inside parentheses (empty if placeholder) */
    designation: string
    /** Value after the colon (e.g., "ASTM C1036...") */
    value: string
    /** Full original text */
    fullText: string
}

/**
 * Options for parsing designation text
 */
export interface ParseDesignationOptions {
    /** Whether to treat placeholder as empty string (default: true) */
    treatPlaceholderAsEmpty?: boolean
}

/**
 * Parses keyed text with drawing designation format.
 *
 * Expected format: "{prefix} ({designation}): {value}"
 * Example: "A. Clear Annealed Float Glass (insert drawing designation): ASTM C1036..."
 *
 * The designation is always within parentheses before the colon.
 *
 * @param text - The text to parse
 * @param options - Parsing options
 * @returns Parsed structure with prefix, designation, and value
 */
export const parseDesignationText = (
    text: string,
    options: ParseDesignationOptions = {}
): ParsedDesignationText => {
    const { treatPlaceholderAsEmpty = true } = options

    const colonIndex = text.indexOf(':')
    const textBeforeColon = colonIndex !== -1 ? text.substring(0, colonIndex) : text

    const openParenIndex = textBeforeColon.indexOf('(')
    const closeParenIndex = textBeforeColon.lastIndexOf(')')

    // Handle case with no parentheses before colon
    if (openParenIndex === -1 || closeParenIndex === -1 || closeParenIndex <= openParenIndex) {
        const prefix = colonIndex !== -1 ? text.substring(0, colonIndex).trim() : text.trim()
        const value = colonIndex !== -1 ? text.substring(colonIndex + 1).trim() : ''

        return {
            prefix,
            designation: '',
            value,
            fullText: text,
        }
    }

    // Extract components
    const prefix = textBeforeColon.substring(0, openParenIndex).trim()
    let designation = textBeforeColon.substring(openParenIndex + 1, closeParenIndex).trim()
    const value = colonIndex !== -1 ? text.substring(colonIndex + 1).trim() : ''

    // Handle placeholder - both UI placeholder and server placeholder should be treated as empty
    const isPlaceholder =
        designation === DESIGNATION_PLACEHOLDER ||
        designation.toLowerCase() === SERVER_DESIGNATION_PLACEHOLDER.toLowerCase()

    if (treatPlaceholderAsEmpty && isPlaceholder) {
        designation = ''
    }

    return {
        prefix,
        designation,
        value,
        fullText: text,
    }
}

/**
 * Reconstructs keyed text with a new designation value.
 * Maintains the original format and structure.
 * 
 * @param parsed - Previously parsed text structure
 * @param newDesignation - New designation value to inject
 * @returns Reconstructed text with new designation
 */
export const reconstructDesignationText = (
    parsed: ParsedDesignationText,
    newDesignation: string
): string => {
    const designationValue = newDesignation.trim() || DESIGNATION_PLACEHOLDER
    let result = `${parsed.prefix} (${designationValue})`

    if (parsed.value) {
        result += `: ${parsed.value}`
    }

    return result
}

/**
 * Updates designation value in the original text.
 * Convenience method that combines parse and reconstruct.
 * 
 * @param text - Original text
 * @param newDesignation - New designation value
 * @returns Updated text with new designation
 */
export const updateDesignationInText = (
    text: string,
    newDesignation: string
): string => {
    const parsed = parseDesignationText(text)
    return reconstructDesignationText(parsed, newDesignation)
}
